import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
  OnInit,
  OnDestroy,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SeoService } from './core/seo/seo.service';
import { ThemeService } from './core/services/theme.service';
import { LucideLegacyIconService } from './core/services/lucide-legacy-icon.service';
import { LoaderComponent } from './shared/components/loader/loader';
import { LoaderService } from './shared/services/loader.service';
import {
  SeoMetaTag,
  SeoMetaTagService,
} from './core/services/seo-meta-tag.service';
import { MetaTagService } from './core/services/meta-tag.service';
import { Subscription } from 'rxjs';
import { CookieConsentBannerComponent } from './shared/components/cookie-consent-banner/cookie-consent-banner';
import { CookieConsentService } from './core/services/cookie-consent.service';
import { SessionTimeoutService } from './core/services/session-timeout.service';
import { SuccessPopupComponent } from './shared/components/success-popup/success';

@Component({
    selector: 'app-root',
    imports: [CommonModule, RouterOutlet, LoaderComponent, CookieConsentBannerComponent, SuccessPopupComponent],
    template: `
      <app-loader></app-loader>
      <router-outlet></router-outlet>
      <app-cookie-consent-banner></app-cookie-consent-banner>
      <app-success-popup [show]="sessionTimeoutService.warningVisible()" [appendToBody]="true"
        title="Session inactivity detected" message="You have been idle for more than 5 minutes."
        okLabel="Extend Session" cancelLabel="Logout" [okLoading]="sessionTimeoutService.isExtendingSession()"
        [showCancel]="true" [autoCloseOnOk]="false" iconClass="ti ti-clock-hour-5"
        [closeOnBackdropClick]="false" [closeOnEscape]="false" (ok)="sessionTimeoutService.extendSession()"
        (close)="sessionTimeoutService.logoutNow()">
        <p style="margin: 0; text-align: center;">
          Your session will close automatically in
          <strong>{{ sessionTimeoutService.formattedCountdown() }}</strong>
          unless you extend it.
        </p>
      </app-success-popup>
      
      <!-- Debug buttons (commented out for production) -->
      <!--
      <div style="position: fixed; top: 10px; right: 10px; z-index: 10000;">
        <button (click)="testLoader()" style="margin-right: 10px; padding: 5px 10px; background: #3f51b5; color: white; border: none; border-radius: 4px;">
          Test Loader
        </button>
        <button (click)="resetLoader()" style="padding: 5px 10px; background: #f44336; color: white; border: none; border-radius: 4px;">
          Reset Loader
        </button>
      </div>
      -->
    `,
    styles: [`
      :host {
        display: block;
        width: 100%;
        height: 100vh;
      }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
    appTitle = signal(' Admin');
    angularVersion = signal('20.3.7');
    
    private seoService = inject(SeoService);
    private themeService = inject(ThemeService);
    private lucideLegacyIconService = inject(LucideLegacyIconService);
    private loaderService = inject(LoaderService);
    private router = inject(Router);
    private seoMetaTagService = inject(SeoMetaTagService);
    private metaTagService = inject(MetaTagService);
    private cookieConsentService = inject(CookieConsentService);
    readonly sessionTimeoutService = inject(SessionTimeoutService);
    private navigationSubscription?: Subscription;
    private publicSeoTags = new Map<string, SeoMetaTag>();

    constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {}

    ngOnInit(): void {
      this.themeService.initializeTheme();
      this.lucideLegacyIconService.start();

      // Ensure loader is reset on app init
      this.loaderService.reset();
      
      // Initialize default SEO and structured data
      this.seoService.updateSeoData({
        title: 'RMA Property Manager - Rental Management System',
        description: 'Comprehensive property rental management system for landlords, tenants, and property managers. Streamline rent collection, maintenance requests, lease management, and tenant communications in one powerful platform.',
        type: 'website'
      });

      // Add website structured data
      this.seoService.addStructuredData(this.seoService.generateWebsiteStructuredData());
      
      // Add organization structured data
      this.seoService.addStructuredData(this.seoService.generateOrganizationStructuredData());

      if (isPlatformBrowser(this.platformId)) {
        this.cookieConsentService.init();
        this.sessionTimeoutService.init();
        this.loadPublicSeoMetaTags();
        this.navigationSubscription = this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.applySeoMetaTagForRoute(event.urlAfterRedirects);
          }
        });
      }
    }

    ngOnDestroy(): void {
      this.lucideLegacyIconService.stop();
      this.navigationSubscription?.unsubscribe();
    }

    private applySeoMetaTagForRoute(url: string): void {
      const pageKey = this.resolvePageKey(url);
      if (!pageKey) {
        return;
      }

      const tag = this.publicSeoTags.get(pageKey.toLowerCase());
      if (tag?.isActive) {
        this.metaTagService.updateMetaTags(tag);
      }
    }

    private loadPublicSeoMetaTags(): void {
      this.seoMetaTagService.getActiveSeoMetaTags().subscribe({
        next: (response) => {
          const tags = response?.response ?? [];
          this.publicSeoTags = new Map(
            tags
              .filter(tag => !!tag?.pageKey)
              .map(tag => [tag.pageKey.trim().toLowerCase(), tag])
          );
          this.applySeoMetaTagForRoute(this.router.url);
        },
        error: () => {
          // Keep the default SEO metadata when the public SEO config cannot be loaded.
          this.publicSeoTags.clear();
          this.applySeoMetaTagForRoute(this.router.url);
        }
      });
    }

    private resolvePageKey(url: string): string | null {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const trimmed = cleanUrl.replace(/^\/+|\/+$/g, '');

      if (!trimmed || trimmed === 'home') {
        return 'home';
      }

      const firstSegment = trimmed.split('/')[0];
      const directMap: Record<string, string> = {
        'popular-blogs': 'blog',
        properties: 'properties',
        login: 'login',
        register: 'register',
        contact: 'contact',
        about: 'about',
        'privacy-policy': 'privacy-policy',
        reviews: 'reviews',
        pricing: 'pricing',
        search: 'search',
        
      };

      return directMap[firstSegment] || firstSegment;
    }

    // Debug methods (commented out for production)
    /*
    testLoader() {
      this.loaderService.show();
      setTimeout(() => {
        this.loaderService.hide();
      }, 3000);
    }

    resetLoader() {
      this.loaderService.reset();
    }
    */
}
